import './App.css';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import furball from 'wa-furball';
import { ILoginData, StateContext } from './Components/StateContext';
import Pages, { PageItems } from './Components/Pages';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material';
import { CheckIfAlreadyLoggedIn } from './Components/Services/APICalls/apicalls';
import { LanguageProvider } from './Components/Services/LanguageContext';
import { ThemeProvider, useTheme } from './Components/Services/ThemeContext';

export interface IContextData {
    loginData: ILoginData;
    setLoginData: Function;
    page: number;
    setPage: Function;
}

function AppContent() {
    const [loginData, setLoginData] = useState<ILoginData>(null);
    const [page, setPage] = useState(PageItems.PROJECTS);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const { isDark } = useTheme();

    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode: isDark ? 'dark' : 'light',
            primary: {
                main: '#00d9ff',
            },
            secondary: {
                main: '#a855f7',
            },
            success: {
                main: '#22c55e',
            },
            error: {
                main: '#ef4444',
            },
            background: {
                default: isDark ? '#0a0a0f' : '#f8fafc',
                paper: isDark ? '#12121a' : '#ffffff',
            },
        },
        typography: {
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
        },
        shape: {
            borderRadius: 10,
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            '&:hover fieldset': {
                                borderColor: '#00d9ff',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: '#00d9ff',
                                boxShadow: '0 0 10px rgba(0, 217, 255, 0.3)',
                            },
                        },
                    },
                },
            },
        },
    }), [isDark]);

    const mounted = useRef(false);

    useEffect(() => {
        if (mounted.current) return;
        CheckIfAlreadyLoggedIn(setLoginData, () => setIsCheckingAuth(false));
        mounted.current = true;
    }, []);

    useEffect(() => {
        const interceptorId = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    setLoginData((current: ILoginData) => {
                        if (current !== null) {
                            furball.info('Sessie verlopen. Log opnieuw in.');
                        }
                        return null;
                    });
                }
                return Promise.reject(error);
            }
        );
        return () => {
            axios.interceptors.response.eject(interceptorId);
        };
    }, []);

    return (
        <MuiThemeProvider theme={muiTheme}>
            <div className='App'>
                <div className='animated-bg' />
                <StateContext.Provider
                    value={
                        {
                            loginData,
                            setLoginData,
                            page,
                            setPage,
                        } as IContextData
                    }
                >
                    <header className='App-header'>
                        {isCheckingAuth && !loginData
                            ? null
                            : <Pages page={!loginData ? (page === PageItems.REGISTER ? PageItems.REGISTER : PageItems.LOGIN) : page} />
                        }
                    </header>
                </StateContext.Provider>
            </div>
        </MuiThemeProvider>
    );
}

function App() {
    return (
        <ThemeProvider>
            <LanguageProvider>
                <AppContent />
            </LanguageProvider>
        </ThemeProvider>
    );
}

export default App;
